var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { ChangeEvent, ChangeEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { isContentfulString } from '@philips-internal/oswald-ui-utils';
class FieldListAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        this.onChange = new ChangeEventListener();
    }
    /**
     * Gets the expression used for the `Add field` link.
     */
    getAddFieldExpression() {
        return this.state.addFieldExpression;
    }
    /**
     * Updates the expression used for the `Add field` link.
     *
     * @param addFieldExpression - The link expression.
     */
    setAddFieldExpression(addFieldExpression) {
        this.setState({ addFieldExpression });
    }
    /**
     * Returns if the field list is enabled.
     */
    isEnabled() {
        return this.state.enabled;
    }
    /**
     * Sets if the field list is enabled.
     *
     * @param enabled - If the field list is enabled.
     */
    setEnabled(enabled) {
        this.setState({ enabled });
    }
    /**
     * Gets the key used to get the label value of each row.
     */
    getLabelKey() {
        return this.state.labelKey;
    }
    /**
     * Updates the key used to get the label of each row.
     *
     * @param labelKey - The updated label key.
     */
    setLabelKey(labelKey) {
        this.setState({ labelKey });
    }
    /**
     * Gets the component value.
     */
    getValue() {
        return this.state.value;
    }
    /**
     * Updates the component value.
     *
     * @param value - The updated value.
     */
    setValue(value) {
        const { value: oldValue } = this.state;
        this.setState({ value });
        this.onChange.fire(new ChangeEvent({ oldValue, value }));
    }
    /**
     * Checks if the component is read-only.
     */
    isReadOnly() {
        return Boolean(this.state.readOnly);
    }
    /**
     * Sets the read-only state of the component.
     *
     * @param readOnly - A boolean indicating if the component should be read-only.
     */
    setReadOnly(readOnly) {
        this.setState({ readOnly });
    }
    /**
     * Gets the expression used for the `Remove field` link.
     */
    getRemoveFieldExpression() {
        return this.state.removeFieldExpression;
    }
    /**
     * Updates the expression used for the `Remove field` link.
     *
     * @param removeFieldExpression - The link expression.
     */
    setRemoveFieldExpression(removeFieldExpression) {
        this.setState({ removeFieldExpression });
    }
    /**
     * Gets the maximum number of rows.
     */
    getMaxRows() {
        return this.state.maxRows;
    }
    /**
     * Sets the maximum number of rows.
     *
     * @param maxRows - The maximum number of rows.
     */
    setMaxRows(maxRows) {
        this.setState({ maxRows });
    }
    /**
     * Gets the preview amount.
     */
    getPreviewAmount() {
        return this.state.previewAmount;
    }
    /**
     * Sets the preview amount.
     *
     * @param previewAmount - The preview amount.
     */
    setPreviewAmount(previewAmount) {
        this.setState({ previewAmount });
    }
    /**
     * Checks if the `Add field` link should be shown.
     */
    getShowAddField() {
        return this.state.showAddField;
    }
    /**
     * Sets the visibility of the `Add field` link.
     *
     * @param showAddField - A boolean indicating if the `Add field` link should be shown.
     */
    setShowAddField(showAddField) {
        this.setState({ showAddField });
    }
    /**
     * Checks if the `Remove field` link should be shown.
     */
    getShowRemoveField() {
        return this.state.showRemoveField;
    }
    /**
     * Sets the visibility of the `Remove field` link.
     *
     * @param showRemoveField - A boolean indicating if the `Remove field` link should be shown.
     */
    setShowRemoveField(showRemoveField) {
        this.setState({ showRemoveField });
    }
    /**
     * Gets a list where each member contains a map with row children APIs.
     */
    getRowsAPIs() {
        return Array.from(groupAPIsByIdentifier(this.childrenAPIs).values());
    }
    /**
     * Gets the apis for the row that corresponds to the given event target.
     *
     * @param target - The event target component.
     */
    getRowAPIsByEventTarget(target) {
        return findGroupByEventTarget(groupAPIsByIdentifier(this.childrenAPIs), target);
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FieldListAPI.prototype, "onChange", void 0);
function groupAPIsByIdentifier(apis) {
    const groups = new Map();
    for (const api of Array.from(apis.values())) {
        // @ts-ignore: `.state` access is protected, as we are trying to use it directly from an instance.
        const identifier = String(api.state.identifier);
        if (!isContentfulString(identifier)) {
            continue;
        }
        const group = groups.get(identifier) ?? new Map();
        groups.set(identifier, group.set(api.getAlias(), api));
    }
    return groups;
}
function findGroupByEventTarget(apis, target) {
    for (const group of apis.values()) {
        const options = {
            apis: Array.from(group.values()),
            target,
        };
        if (targetBelongsToGroup(options)) {
            return group;
        }
    }
    return;
}
function targetBelongsToGroup(options) {
    if (!Array.isArray(options.apis)) {
        return false;
    }
    for (const api of options.apis) {
        if (api === options.target) {
            return true;
        }
        const opts = {
            // @ts-ignore: `.childrenAPIs` access is protected, as we are trying to use it directly from an instance.
            apis: Array.from(api.childrenAPIs.values()),
            target: options.target,
        };
        if (targetBelongsToGroup(opts)) {
            return true;
        }
    }
    return false;
}
export { FieldListAPI, };
//# sourceMappingURL=field-list-api.js.map